import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { IPageFilters } from 'src/app/models/filters/interfaces/pageFilters.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

@Component({
  selector: 'app-multi-select-v2',
  templateUrl: './multi-select-v2.component.html',
  styleUrls: ['./multi-select-v2.component.scss'],
})
export class MultiSelectV2Component implements OnInit {
  @Input() public pageFilters: IPageFilters;
  @Input() public withCategories = true;
  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public selectedCategoriesDisplayName: Set<string> = new Set();
  public selectedIds: Set<string>;
  public optionsVisible = false;

  public dynamicCategories: string[];

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters() ?? [];

    this.pageFilters.getAllFiltersOptions().subscribe((options) => {
      const optionsInsideCategories: ISelectOptionGroups[] = [];
      const indexByCategory: { [key: string]: number } = {};

      options.forEach((option) => {
        if (option.categoryName) {
          if (option.categoryName in indexByCategory) {
            optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
          } else if (!this.dynamicCategories.includes(option.categoryName)) {
            optionsInsideCategories.push({
              selectedCount: 0,
              displayName: option.categoryName,
              options: [option],
              isDynamic: false,
            });
            indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
          } else {
            optionsInsideCategories.push({
              selectedCount: 0,
              displayName: option.categoryName,
              options: [option],
              isDynamic: true,
            });
            indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
          }
        } else {
          optionsInsideCategories.push({
            options: [option],
          } as ISelectOptionGroups);
        }
      });

      this.dynamicCategories.forEach((category) => {
        if (!(category in indexByCategory)) {
          optionsInsideCategories.push({
            selectedCount: 0,
            displayName: category,
            options: [],
            isDynamic: true,
          });
        }
      });

      this.optionsInsideCategories$.next(optionsInsideCategories);
    });

    this.pageFilters.onUpdate$.subscribe(() => {
      const newSelectedCategoriesDisplayName: Set<string> = new Set();
      const newSelectedIds: Set<string> = new Set();

      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
        if (option.categoryName) {
          newSelectedCategoriesDisplayName.add(option.categoryName);
        }
      });
      this.selectedIds = newSelectedIds;
      this.selectedCategoriesDisplayName = newSelectedCategoriesDisplayName;
    });
  }

  public toggleOptions() {
    this.optionsVisible = !this.optionsVisible;
    if (!this.optionsVisible) this.optionOnSelection.emit(this._getSelectedOptions());
  }

  public hideOptions() {
    this.optionsVisible = false;
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
    this.pageFilters.updateFilterFromUserSelection(undefined, this._getSelectedOptions());
  }

  public clearSelections(): void {
    this.selectedIds = new Set<string>();
    const selectedOptions = this._getSelectedOptions();
    if (this.optionsVisible == false) {
      this.optionOnSelection.emit(selectedOptions);
    }
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }
}
